import { ChangeDetectionStrategy, Component, computed, inject, model, Signal, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import {
  AtButtonDirective,
  AtHeaderComponent,
  AtIconComponent,
  AtSidenavComponent,
  BREAKPOINTS,
  COMMON_DICTIONARY,
  CommonDictionary,
  ConfigurableAuthService,
  matchMediaQuery,
} from '@anothertrip/anothertrip-ds';
import { filter, map } from 'rxjs';

import { LoadingService } from '../../services/loading.service';
import { DashboardNavComponent } from '../dashboard-nav/dashboard-nav.component';
import { UserOverlaySelectComponent } from '../user-overlay-select/user-overlay-select.component';
import { WorkspaceSelectComponent } from '../workspace-select/workspace-select.component';

import { wantsStickyHeader } from './sticky-header';

@Component({
  selector: 'app-header-wrapper',
  standalone: true,
  imports: [
    AtHeaderComponent,
    AtButtonDirective,
    RouterLink,
    AtSidenavComponent,
    AtIconComponent,
    DashboardNavComponent,
    UserOverlaySelectComponent,
  ],
  templateUrl: './header-wrapper.component.html',
  styleUrl: './header-wrapper.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'header-wrapper-host',
    '[class.is-sticky]': 'isSticky()',
  },
})
export class HeaderWrapperComponent {
  private readonly authService = inject(ConfigurableAuthService);
  private readonly router = inject(Router);
  private readonly loadingService = inject(LoadingService);
  protected readonly dictionary = inject<CommonDictionary>(COMMON_DICTIONARY).header;

  protected readonly isLoggedIn: Signal<boolean> = this.authService.isLoggedIn;

  protected readonly sidenavOpen = model<boolean>(false);
  /** The drawer's id, so the header's menu button can point `aria-controls` at it. */
  protected readonly sidenavPanelId = 'dashboard-nav-drawer';

  protected readonly isMobile = matchMediaQuery(`(max-width: ${BREAKPOINTS.XS}px)`);

  /**
   * The menu drawer, for every signed-in creator who can see this header (AT-2637).
   *
   * Not gated on width. The app shell already hides this header wherever the dashboard sidebar is
   * standing, so whenever the header renders there is no sidebar on screen: a page outside the
   * dashboard layout (subscription, terms, a public campaign) at any width, or any page below the
   * sidebar breakpoint. Gating on width as well left those pages with no menu at all on a laptop.
   */
  protected readonly showSidenav = computed<boolean>(() => this.isLoggedIn());

  private readonly isStickyRoute = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => wantsStickyHeader(this.router.routerState.snapshot.root))
    ),
    { initialValue: false }
  );

  /**
   * Pinned on the landing page only, where the header holds the only sign-in link (AT-2626; see `sticky-header.ts`).
   * Never for a signed-in creator: below the sidebar breakpoint this header sits above dashboard pages that pin their
   * own toolbars to the top of the viewport.
   */
  protected readonly isSticky = computed<boolean>(() => this.isStickyRoute() && !this.isLoggedIn());

  protected readonly isSignIn = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects.includes('/login'))
    )
  );

  protected readonly showLoadingBar: Signal<boolean> = this.loadingService.isLoading;

  protected toggleSidenav(): void {
    this.sidenavOpen.update((prev) => !prev);
  }
}
